import { useState } from 'react'
import { CalcResult, RackMeta, DcMeta, rackLabel, CABLE_LIMITS, HistoryEntry } from '../../hooks/useCalculatorLogic'

export function SummaryPanel({
  result, error, src, tgt, dcCode, cable, racks, currentDc, history,
  setSrc, setTgt, setCable, setResult, setError
}: {
  result: CalcResult | null
  error: string | null
  src: RackMeta | null
  tgt: RackMeta | null
  dcCode?: string
  cable: string
  racks: RackMeta[]
  currentDc?: DcMeta
  history: HistoryEntry[]
  setSrc: (r: RackMeta) => void
  setTgt: (r: RackMeta) => void
  setCable: (c: string) => void
  setResult: (r: CalcResult | null) => void
  setError: (e: string | null) => void
}) {
  const [copied, setCopied] = useState(false)

  const cableMeta = CABLE_LIMITS[cable]
  const lengthOk = result ? result.total_length <= cableMeta.maxM : true
  const lengthPct = result ? Math.min(100, (result.total_length / cableMeta.maxM) * 100) : 0

  const handleCopy = () => {
    if (!result || !src || !tgt || !dcCode) return
    const lines = [
      `ЦОД: ${dcCode}`,
      `${rackLabel(src, currentDc)} → ${rackLabel(tgt, currentDc)}`,
      `Кабель: ${cableMeta.label}`,
      `Длина: ${result.total_length.toFixed(2)} м`,
      result.warning ? `! ${result.warning}` : '',
      '',
      ...result.route.map((p, i) => `${i + 1}. ${p.description}  [${p.x.toFixed(1)}, ${p.y.toFixed(1)}, ${p.z.toFixed(1)}]`),
    ].filter(Boolean).join('\n')
    navigator.clipboard.writeText(lines).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {error && (
        <div className="m-3 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
          {error}
        </div>
      )}

      {result && src && tgt && (
        <>
          <div className="px-3 py-3 border-b border-border">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-baseline gap-1.5">
                <span className={`text-2xl font-mono font-bold ${lengthOk ? 'text-status-normal' : 'text-status-critical'}`}>
                  {result.total_length.toFixed(2)}
                </span>
                <span className="text-text-muted text-xs font-mono">м</span>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="text-[11px] text-text-muted hover:text-text-primary border border-border rounded px-1.5 py-0.5 transition-colors shrink-0"
              >
                {copied ? '✓' : '⎘'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 bg-bg-input rounded overflow-hidden">
                <div
                  className={`h-full rounded transition-all ${lengthOk ? 'bg-status-normal' : 'bg-status-critical'}`}
                  style={{ width: `${lengthPct}%` }}
                />
              </div>
              <span className="text-[10px] text-text-muted tabular-nums shrink-0">
                {lengthPct.toFixed(0)}%
              </span>
            </div>
            <div className="text-[10px] text-text-muted mt-1">{cableMeta.label} ≤ {cableMeta.maxM}м</div>
          </div>

          {result.warning && (
            <div className="px-3 py-2 text-[11px] text-status-warning bg-yellow-500/5 border-b border-yellow-500/15 flex gap-1.5">
              <span className="shrink-0">⚠</span><span>{result.warning}</span>
            </div>
          )}

          <div className="px-3 py-2 border-b border-border">
            <div className="text-[10px] text-text-muted uppercase tracking-wider mb-2">
              Маршрут · {result.route.length} точек
            </div>
            <div className="flex flex-col gap-0">
              {result.route.map((p, i) => (
                <div key={i} className="flex gap-2 py-1.5 border-b border-border last:border-0 relative">
                  {i < result.route.length - 1 && (
                    <div className="absolute left-[9px] top-7 bottom-0 w-px bg-border z-0" />
                  )}
                  <div className={`w-[18px] h-[18px] rounded-full border shrink-0 flex items-center justify-center text-[8px] font-bold z-10
                    ${i === 0 ? 'border-status-warning text-status-warning' :
                      i === result.route.length - 1 ? 'border-status-normal text-status-normal' :
                      'border-border text-text-muted'}`}>
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] text-text-primary leading-tight">{p.description}</div>
                    <div className="text-[10px] text-text-muted font-mono mt-0.5">
                      {p.x.toFixed(1)}, {p.y.toFixed(1)}, z:{p.z.toFixed(1)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!result && !error && (
        <div className="px-3 py-4 text-center text-[11px] text-text-muted">
          {racks.length === 0 && dcCode
            ? <span className="text-status-warning">В ЦОД «{dcCode}» нет опубликованных стоек</span>
            : 'Выберите стойки и нажмите «Рассчитать»'
          }
        </div>
      )}

      {history.length > 0 && (
        <div className="px-3 py-2 border-t border-border mt-auto">
          <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1.5">История</div>
          <div className="flex flex-col gap-1">
            {history.map(h => (
              <button
                key={h.id}
                type="button"
                onClick={() => {
                  setSrc(h.src); setTgt(h.tgt); setCable(h.cableType)
                  setResult({ total_length: h.length, warning: h.warning, route: h.route })
                  setError(null)
                }}
                className="flex items-center gap-1.5 px-2 py-1 text-left text-[11px] rounded border border-border hover:bg-bg-hover transition-colors"
              >
                <span className={h.warning ? 'text-status-warning' : 'text-status-normal'}>
                  {h.warning ? '⚠' : '✓'}
                </span>
                <span className="flex-1 font-mono truncate text-text-secondary">
                  {h.srcLabel} → {h.tgtLabel}
                </span>
                <span className="text-text-muted shrink-0 tabular-nums">{h.length.toFixed(1)}м</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
